import React, { useEffect } from 'react';
import styled from 'styled-components';

import { ComparisonSection } from '../components/ComparisonSection';
import { ContentGrid } from '../components/Product/ContentGrid';
import { Encouragements } from '../components/Encouragements';
import { FaqSection } from '../components/Faq/FaqSection';
import { Featured } from '../components/Featured';
import { IngredientsSection } from '../components/Product/IngredientsSection';
import { LabelSection } from '../components/Product/LabelSection';
import { OtherProducts } from '../components/Product/OtherProducts';
import { ReviewsSection } from '../components/Product/ReviewsSection';
import { SeoTags } from '../components/SeoTags';
import { ServiceUsps } from '../components/ServiceUsps';
import { SubscriptionSection } from '../components/SubscriptionSection';
import { Top } from '../components/Product/Top';
import { VideoSection } from '../components/Product/VideoSection';
import { VisibilityChecker } from '/src/components/VisibilityChecker';
import { log } from '../utils/logs';
import { AffiliateTop } from '../components/Product/AffiliateTop';
import { VideoEngagement } from '../components/Product/VideoEngagement';
import { SchemaOrg } from '../components/SchemaOrg';
import { useDiscounts } from '../hooks/useDiscounts';
import { DiscountBanner } from '../components/DiscountBanner';
import { media } from '../utils/styles';

const StyledDiscountBanner = styled(DiscountBanner)`
  margin-top: 110px;
  ${media.phone} {
    margin-top: 125px;
  }
`;

const ProductPage = ({
  pageContext: { product, pricingType, affProductPage, subscription = false },
}) => {
  useEffect(() => {
    log('view_item', {
      id: product.shopify_product.id,
      name: product.shopify_product.title,
      price: product.shopify_product.variants[0].price,
    });
    // eslint-disable-next-line
  }, []);

  const [banner, bannerType] = useDiscounts();

  if (!(product.shopify_product.variants.length > 1)) {
    return <></>;
  }

  return (
    <>
      <SeoTags
        title={product.title}
        description={product.short_description}
        meta={[
          {
            property: 'og:image',
            content: product.images[0].image.url,
          },
        ]}
        product={product}
      />
      <SchemaOrg product={product} />
      {!!banner && <StyledDiscountBanner banner={banner} />}
      <VisibilityChecker id="ProductPage-Top">
        {affProductPage ? (
          <AffiliateTop product={product} pricingType={pricingType} />
        ) : (
          <Top
            product={product}
            subscription={subscription}
            banner={banner}
            bannerType={bannerType}
          />
        )}
      </VisibilityChecker>
      <VisibilityChecker id="ProductPage-Featured">
        <Featured heading={product.featured_section_headline} />
      </VisibilityChecker>
      {!!product.engagement_video_cover &&
        !!product.engagement_video_file &&
        !!product.video_engagement_heading && (
          <VideoEngagement
            cover={product.engagement_video_cover}
            videoUrl={product.engagement_video_file.url}
            heading={product.video_engagement_heading}
          />
        )}
      {!!product.ingredients.length && (
        <VisibilityChecker id="ProductPage-IngredientsSection">
          <IngredientsSection
            title={product.ingredients_section_heading}
            description={product.ingredients_section_description}
            ingredients={product.ingredients}
          />
        </VisibilityChecker>
      )}
      {!!product.comparable_list_pros.length &&
        !!product.comparable_list_cons.length &&
        !!product.comparable_title_pros &&
        !!product.comparable_title_cons && (
          <VisibilityChecker id="ProductPage-ComparisonSection">
            <ComparisonSection
              cardBg={product.comparable_background_color}
              productColor={product.product_card_background}
              prosList={product.comparable_list_pros}
              consList={product.comparable_list_cons}
              prosTitle={product.comparable_title_pros}
              consTitle={product.comparable_title_cons}
            />
          </VisibilityChecker>
        )}
      {!!product.video_section_heading && !!product.youtube_video.html && (
        <VisibilityChecker id="ProductPage-VideoSection">
          <VideoSection
            heading={product.video_section_heading}
            videoHtml={product.youtube_video.html}
          />
        </VisibilityChecker>
      )}
      {!!product.content_grid.length && (
        <VisibilityChecker id="ProductPage-ContentGrid">
          <ContentGrid sections={product.content_grid} />
        </VisibilityChecker>
      )}
      {!!product.supplement_facts_image &&
        !!product.supplement_facts_section_text &&
        !!product.supplement_facts_section_heading && (
          <VisibilityChecker id="ProductPage-LabelSection">
            <LabelSection
              heading={product.supplement_facts_section_heading}
              text={product.supplement_facts_section_text}
              image={product.supplement_facts_image}
              primaryColor={product.primary_color}
            />
          </VisibilityChecker>
        )}
      {!!product.reviews.length && (
        <VisibilityChecker id="ProductPage-ReviewsSection">
          <ReviewsSection
            average={product.average_customers_rating || 4.9}
            reviews={product.reviews}
          />
        </VisibilityChecker>
      )}
      <VisibilityChecker id="ProductPage-ServiceUsps">
        <ServiceUsps asSection />
      </VisibilityChecker>
      {!!product.faq_section_heading && !!product.faq.length && (
        <VisibilityChecker id="ProductPage-FaqSection">
          <FaqSection heading={product.faq_section_heading} faq={product.faq} />
        </VisibilityChecker>
      )}
      {!!product.similar_products_section_heading && !!product.similar_products.length && (
        <VisibilityChecker id="ProductPage-OtherProducts">
          <OtherProducts
            heading={product.similar_products_section_heading}
            products={product.similar_products}
          />
        </VisibilityChecker>
      )}
      <VisibilityChecker id="ProductPage-SubscriptionSection">
        <SubscriptionSection />
      </VisibilityChecker>
      <Encouragements />
    </>
  );
};

export default ProductPage;
